import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { FileUpload } from '../../api/services';
import { toast } from 'react-toastify';
import ReactPlayer from 'react-player';

interface FileUploadContainerProps {
  onFileSelect: (fileData: { url: string; name: string; size: number; type: string } | null) => void;
  foldername: string;
  existingImage?: string | null; // Prop for existing image
  type?: string | null;
  onDelete: () => void; // Function to handle image deletion
}

const FileUploadContainer: React.FC<FileUploadContainerProps> = ({
  onFileSelect,
  foldername,
  existingImage,
  type,
  onDelete,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (existingImage) {
      const imageUrl = `https://dprstorage.b-cdn.net${existingImage}`;
      setPreview(imageUrl);
    }
    if (type) {
      setFileType(type);
    }
  }, [existingImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpeg', '.jpg'],
      'video/*': ['.mp4', '.mov'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1, // Restrict to only one file
    onDrop: async (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        toast.error('Please select a correct format file type.');
        return;
      }

      const file = acceptedFiles[0];
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setPreview(previewUrl);
        setFileType(file.type.split('/')[0]);
        setUploadStatus('uploading');
    
        try {
          const response = await FileUpload(foldername, file);
          const fileData = {
            url: response.data.url,
            name: response.data.originalName, 
            size: response.data.size,
            type: response.data.type,
          };
          onFileSelect(fileData); // Pass the correctly formatted object
          setUploadStatus('success');
          toast.success('File uploaded successfully');
        } catch (error) {
          console.error('Error uploading file:', error);
          setUploadStatus('error');
          toast.error('Error uploading file. Please try again.');
          onFileSelect(null);
        }
      }
    },
  });

  const borderColor =
    uploadStatus === 'success'
      ? '#4CAF50'
      : uploadStatus === 'error'
      ? '#F44336'
      : isDragActive
      ? '#2196f3'
      : '#24272C90';

  const handleRemoveImage = () => {
    setPreview(null);
    setFileType(null);
    onDelete();
    onFileSelect(null);
  };

  console.log(fileType, preview);

  return (
    <Box
      {...getRootProps()}
      sx={{
        height: '150px',
        border: `2px dashed ${borderColor}`,
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        position: 'relative',
        textAlign: 'center',
      }}
    >
      <input {...getInputProps()} />
      {uploadStatus === 'uploading' ? (
        <CircularProgress
          size={24}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      ) : (
        <>
          {preview ? (
            <Box sx={{ position: 'relative', width: 'auto', height: '100%', maxWidth: '100%', display: "flex", justifyContent: "center", alignItems: "center"  }}>
              {fileType === 'image' && (
                <Box sx={{ width: '90%', height: '100%', overflow: 'hidden', borderRadius: '10px' }}>
                  <img
                  src={preview}
                  alt="Selected"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  />
                </Box>
              )}
              {fileType === 'video' && (
                <ReactPlayer
                  url={preview}
                  controls
                  muted={true}
                  width="100%"
                  height="100%"
                  style={{
                    objectFit: "contain",
                  }}
                  config={{
                    file: {
                      attributes: {
                        controlsList: "nodownload",
                      },
                    },
                  }}
                />
              )}
              {fileType === 'document' && (
                <iframe
                  src={preview}
                  style={{
                    width: '95%', 
                    height: '95%', 
                    borderRadius: '10px',
                  }}
                />
              )}
              <IconButton
                onClick={handleRemoveImage}
                sx={{ position: 'absolute', top: 0, right: 0, color: 'red' }}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
              <AddPhotoAlternateIcon sx={{ fontSize: '40px', color: '#6563FF', marginBottom: '8px' }} />
              <Typography variant="caption" sx={{ color: '#6563FF' }}>
                Drag & drop a file here, or click to select one
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default FileUploadContainer;
